/**
 * Atlas — unified guidance layer: shared types.
 *
 * Mission definitions live in ./missions/*.ts as pure data (no JSX).
 * All persisted state is read/written exclusively via ./adapters/onboardingStateAdapter.
 */

export const ATLAS_BRAND = 'Atlas';
export const ATLAS_DESCRIPTOR = 'your AI navigator';
export const ATLAS_BRAND_FULL = 'Relix Atlas';

/** Canonical mission identifiers. Keep stable — they are storage keys. */
export type AtlasMissionId =
  | 'welcome'
  | 'create-organization'
  | 'add-first-product'
  | 'wire-integrations'
  | 'load-analytics-data'
  | 'first-report'
  | 'first-baseline'
  | 'calendar-connect'
  | 'post-full-update'
  | 'forecast-tour'
  | 'releases-tour';

/** Terminal step markers shared across missions. */
export const TERMINAL_COMPLETED = 'completed' as const;
export const TERMINAL_SKIPPED = 'skipped' as const;
export type AtlasTerminalStep = typeof TERMINAL_COMPLETED | typeof TERMINAL_SKIPPED;

export interface AtlasMissionStep {
  /** Stable step id used as storage value (e.g. `'welcome'`, `'timeframe'`). */
  id: string;
  /** Card title shown in the FeatureCoach. */
  title: string;
  /** Plain-text body shown inside the coach. */
  body: string;
  /** DOM id to scroll to when this step becomes active. Convention: `#<page>-<section>-anchor`. */
  anchorId?: string;
  /** If set, Atlas will navigate here before showing the step. */
  route?: string;
  /** Override for the primary button label (defaults to "Next" / "Done" on last step). */
  primaryLabel?: string;
}

export interface AtlasMission {
  id: AtlasMissionId;
  /** Short label shown in the Dock mission list. */
  title: string;
  /** One-line description in the Dock. */
  description: string;
  /** Kicker rendered above the tour title. Keep "Guided tour" for consistency with Forecast/Releases. */
  kicker: string;
  /** Approximate minutes to complete (shown in the Dock). */
  estimatedMinutes?: number;
  /** Route that owns the tour (where the GuidedTour is rendered). */
  entryRoute: string;
  /** Ordered steps. Empty for the `welcome` modal mission. */
  steps: AtlasMissionStep[];
  /**
   * Single source of truth for the FeatureCoach `featureKey` used by this mission.
   * New missions must NOT split this key (see benchmark analysis).
   */
  featureKey: string;
  /**
   * Identifies which adapter owns storage for this mission:
   *  - `'atlas'`     — stored via atlasMissionStepKey (new missions)
   *  - `'forecast'`  — delegates to getForecastTourStep / setForecastTourStep
   *  - `'releases'`  — delegates to getReleasesTourStep / setReleasesTourStep
   *  - `'journey'`   — delegates to getPostUpdateJourneyStepForProduct
   *  - `'welcome'`   — delegates to WELCOME_TOUR_COMPLETED_KEY (single boolean)
   *  - `'derived'`   — not persisted; `completed` is computed from AtlasContext
   *                    (e.g. `create-organization`, `add-first-product`).
   */
  storage: 'atlas' | 'forecast' | 'releases' | 'journey' | 'welcome' | 'derived';
  /**
   * When true, the Atlas launcher glows while this mission is the current one.
   * Reserved for blocking setup steps only (no product / no Jira) — see
   * `AtlasDock` for the consumer and `chooseCurrentMission` for the priority
   * order. Keep the count small to avoid visual noise.
   */
  critical?: boolean;
  /**
   * Optional gating permission. When set, missions are filtered out of the
   * registry view (Dock + `chooseCurrentMission`) for users who don't have
   * the corresponding permission. Currently used by `first-report` so members
   * (no `reports.access`) never see the Reports tour. The string is a
   * `PermissionKey` from `src/shared/permissions.ts` — kept as a plain string
   * here to avoid pulling the permissions module into Atlas types (circular).
   */
  requiredPermission?: string;
}

/**
 * Runtime state derived each render (not persisted). Drives `chooseCurrentMission`.
 */
export interface AtlasContext {
  orgId: string | null;
  productId: string | null;
  pathname: string;
  /** True once the user belongs to at least one organization. */
  hasOrg: boolean;
  /** True once at least one product is selected for the current org. */
  hasProduct: boolean;
  hasJira: boolean;
  hasSharePoint: boolean;
  hasAnalyticsData: boolean;
  postUpdateJourneyActive: boolean;
  welcomeCompleted: boolean;
  /**
   * Calendar onboarding checkpoints (per-product, persisted). Together with
   * `hasSharePoint`, they drive the derived completion of `calendar-connect`
   * so the Atlas Dock flips to Done only after real configuration steps, not
   * after clicking Next through the guided tour.
   */
  hasCalendarSynced: boolean;
  hasOpenedAbsenceKeywords: boolean;
  hasOpenedPublicHolidayKeywords: boolean;
  hasVisitedReleases: boolean;
}

export interface AtlasMissionState {
  missionId: AtlasMissionId;
  /** Current step id, or a terminal marker. `undefined` when not yet started. */
  step: string | undefined;
  completed: boolean;
  skipped: boolean;
  /** Snooze expiry in ms since epoch. `null` when not snoozed. */
  snoozedUntil: number | null;
}

export interface AtlasTelemetryPayload {
  missionId?: AtlasMissionId;
  stepId?: string;
  orgId?: string | null;
  productId?: string | null;
  durationMs?: number;
  reason?: string;
}

export type AtlasTelemetryEvent =
  | 'atlas.mission.started'
  | 'atlas.step.completed'
  | 'atlas.mission.completed'
  | 'atlas.mission.skipped'
  | 'atlas.guidance.dismissed'
  | 'atlas.navigator.restored'
  | 'atlas.dock.opened'
  | 'atlas.dock.closed'
  | 'atlas.welcome.shown'
  | 'atlas.welcome.dismissed';
